import {
  Injectable,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { create, Delta } from 'jsondiffpatch';
import { BuilderService } from '../../services/builder/builder.service';
import { ClipboardService } from './clipboard.service';
import { ModuleStructureObject } from '../../api/interfaces';

@Injectable()
export class HistoryService {
  private builderService = inject(BuilderService);
  private clipboardService = inject(ClipboardService);

  private historySnapshot: ModuleStructureObject | undefined;
  private currentHistoryIndex = signal(0);
  private patcher = create({
    objectHash: function (obj: object, index) {
      return '$$index:' + index;
    },
  });
  private history: Delta[] = [];
  private readonly historyLimit = 30;

  readonly undoDisabled = computed(() => {
    return this.currentHistoryIndex() === 0;
  });

  readonly redoDisabled = computed(() => {
    return this.currentHistoryIndex() >= this.history.length - 1;
  });

  private enableEffects = true;

  constructor() {
    effect(() => {
      const equals = isEqual(
        this.historySnapshot,
        this.builderService.moduleStructure(),
      );

      if (equals || !this.enableEffects) {
        return;
      }

      const newDiff = this.patcher.diff(
        this.historySnapshot,
        this.builderService.moduleStructure(),
      );

      const currentHistoryIndex = untracked(this.currentHistoryIndex);

      if (currentHistoryIndex !== this.history.length - 1) {
        this.history.splice(
          this.currentHistoryIndex() + 1,
          this.history.length - 1,
        );
        // this.history.push(_.cloneDeep(this.cssGrid.gridTiles()));
      }

      this.increasCurrentHistoryIndex();
      this.history = [...this.history, cloneDeep(newDiff)];

      if (this.history.length > this.historyLimit) {
        this.history.shift();
        this.history = [...this.history];
      }

      this.historySnapshot = cloneDeep(this.builderService.moduleStructure());
    });
  }

  private increasCurrentHistoryIndex() {
    // console.log('incremento index');
    this.currentHistoryIndex.set(this.currentHistoryIndex() + 1);
  }

  private decreaseCurrentHistoryIndex() {
    // console.log('decremento index');
    this.currentHistoryIndex.set(this.currentHistoryIndex() - 1);
  }

  setupStartingComponent(data: ModuleStructureObject) {
    this.historySnapshot = cloneDeep(data);
    this.history = [this.patcher.diff(null, this.historySnapshot)];
    this.currentHistoryIndex.set(0);
  }

  undo() {
    if (this.currentHistoryIndex() > 0) {
      const unpatched = this.patcher.unpatch(
        this.builderService.moduleStructure(),
        this.history[this.currentHistoryIndex()],
      ) as ModuleStructureObject;

      // console.log('PRIMA', this.currentHistoryIndex(), this.history);
      this.decreaseCurrentHistoryIndex();
      // console.log('DOPO', this.currentHistoryIndex(), this.history);

      this.historySnapshot = cloneDeep(unpatched);
      this.builderService.moduleStructure.set(cloneDeep(unpatched));
    }
  }

  redo() {
    if (
      this.currentHistoryIndex() < this.history.length - 1 &&
      this.currentHistoryIndex() >= 0
    ) {
      this.increasCurrentHistoryIndex();

      const patched = this.patcher.patch(
        this.builderService.moduleStructure(),
        this.history[this.currentHistoryIndex()],
      ) as ModuleStructureObject;

      this.builderService.moduleStructure.set(cloneDeep(patched));
      this.historySnapshot = cloneDeep(patched);
    }
  }

  reset() {
    this.enableEffects = false;

    const resetObject: ModuleStructureObject = {
      root: {
        _type: 'root',
        children: [],
      },
    };

    this.builderService.moduleStructure.set(resetObject);
    this.patcher = create({
      objectHash: function (obj: object, index) {
        return '$$index:' + index;
      },
    });
    this.historySnapshot = cloneDeep(resetObject);

    this.history = [this.patcher.diff(null, this.historySnapshot)];
    this.currentHistoryIndex.set(0);
    this.enableEffects = true;

    this.clipboardService.clipBoard.set(undefined);
  }
}
